import { alpha, Box, BoxProps, Container, Stack, styled, Typography } from '@mui/material'

import { FC, useEffect, useRef, useState } from 'react'

import { AppConfigProvider } from '../../providers'
import { calculateMinutesSinceLastUpdate, calculateMinutesUntilNextUpdate } from './TrailDataFooter.utils'

const StyledFooterBox = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'isSticky',
})<
  BoxProps & {
    isSticky?: boolean
  }
>(({ theme, isSticky }) => ({
  bottom: -1,
  ...(isSticky && {
    background: alpha('#000', 0.75),
    boxShadow: theme.shadows[4],
  }),
  marginBottom: theme.spacing(1),
  marginTop: theme.spacing(2),
  position: 'sticky',
  width: '100%',
  zIndex: 1000,
}))

const StyledContainer = styled(Container)(({ theme }) => ({
  paddingLeft: theme.spacing(2),
  paddingRight: theme.spacing(2),
  [theme.breakpoints.down('sm')]: {
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
  },
}))

const ExtraDescriptor = styled('span')(({ theme }) => ({
  [theme.breakpoints.down('sm')]: {
    display: 'none',
  },
}))

export const TrailDataFooter: FC = () => {
  const footerContainerRef = useRef<HTMLDivElement>(null)
  const [isSticky, setIsSticky] = useState(false)
  const [now, setNow] = useState(() => new Date())
  const minutesSinceLastUpdate = calculateMinutesSinceLastUpdate(now)
  const minutesUntilNextUpdate = calculateMinutesUntilNextUpdate(now)

  useEffect(() => {
    const intervalId = setInterval(() => setNow(new Date()), 60 * 1000) // 1 minute
    return () => clearInterval(intervalId)
  }, [])

  useEffect(() => {
    const footerContainer = footerContainerRef.current
    if (!footerContainer) {
      return undefined
    }
    const observer = new IntersectionObserver(
      ([e]) => {
        setIsSticky(e.intersectionRatio < 1)
      },
      {
        threshold: [1],
      },
    )
    observer.observe(footerContainer)
    return () => observer.disconnect()
  }, [])

  return (
    <>
      <StyledFooterBox ref={footerContainerRef} isSticky={isSticky}>
        <StyledContainer maxWidth="xl">
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Stack alignItems="center">
              <Typography variant="body1" color="grey" fontSize={{ sm: 16, xs: 12 }}>
                {minutesSinceLastUpdate} mins
              </Typography>
              <Typography variant="caption" color="grey">
                <ExtraDescriptor>since&nbsp;</ExtraDescriptor>last update
              </Typography>
            </Stack>
            {!!AppConfigProvider.notification && (
              <Stack
                alignItems="center"
                direction="row"
                justifyContent="center"
                dangerouslySetInnerHTML={{ __html: AppConfigProvider.notification }}
              ></Stack>
            )}
            <Stack alignItems="center">
              <Typography variant="body1" fontSize={{ sm: 16, xs: 12 }}>
                {minutesUntilNextUpdate} mins
              </Typography>
              <Typography variant="caption">
                <ExtraDescriptor>until&nbsp;</ExtraDescriptor>next update
              </Typography>
            </Stack>
          </Stack>
        </StyledContainer>
      </StyledFooterBox>
    </>
  )
}
TrailDataFooter.displayName = 'TrailDataFooter'
